import { createElement } from 'react'
import Keycloak from 'keycloak-js'
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from 'notistack'

import { SessionExpirySnackbar } from '@/components/SessionExpirySnackbar'
import logger from '@/utils/_legacy/logger'
import { notifyError } from '@/utils/notifyError'

const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 30
const TOKEN_MIN_VALIDITY_SECONDS = 60
const REFRESH_TOKEN_EXPIRY_WARNING_SECONDS = 180 // 3 minutes - when countdown starts

interface TokenManagerCallbacks {
  onTokenRefreshed: (token: string) => void
  onSessionExpired: () => Promise<void>
}

/**
 * Manages Keycloak token refresh, expiry warnings, and refresh intervals
 */
export class TokenManager {
  private keycloakInstance: Keycloak | null = null
  private refreshTimeout: ReturnType<typeof setTimeout> | null = null
  private countdownTimeout: ReturnType<typeof setTimeout> | null = null
  private countdownSnackbarKey: SnackbarKey | null = null
  private countdownActive = false

  constructor(private callbacks: TokenManagerCallbacks) {}

  private closeCountdown(): void {
    if (this.countdownSnackbarKey) {
      closeSnackbar(this.countdownSnackbarKey)
      this.countdownSnackbarKey = null
    }
    this.countdownActive = false
  }

  start(keycloak: Keycloak): void {
    this.stop()
    this.keycloakInstance = keycloak
    this.setupKeycloakCallbacks(keycloak)
    this.scheduleNextRefresh(keycloak)
    this.scheduleCountdown(keycloak)
  }

  stop(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
      this.refreshTimeout = null
    }
    if (this.countdownTimeout) {
      clearTimeout(this.countdownTimeout)
      this.countdownTimeout = null
    }
    this.closeCountdown()
    this.keycloakInstance = null
  }

  private async refreshToken(keycloak: Keycloak): Promise<void> {
    try {
      const refreshed = await keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS)
      if (keycloak.token) {
        if (refreshed) {
          this.callbacks.onTokenRefreshed(keycloak.token)
        }
        this.scheduleNextRefresh(keycloak)
        this.scheduleCountdown(keycloak)
      }
    } catch (error) {
      logger.error(error, 'Token refresh failed')
      notifyError('Your session has expired. Please log in again.')
      await this.callbacks.onSessionExpired()
    }
  }

  private setupKeycloakCallbacks(keycloak: Keycloak): void {
    keycloak.onTokenExpired = async () => {
      await this.handleAccessTokenExpired(keycloak)
    }

    keycloak.onAuthRefreshError = () => {
      notifyError('Session expired. Please log in again.')
      this.closeCountdown()
      this.callbacks.onSessionExpired()
    }

    keycloak.onAuthError = () => this.callbacks.onSessionExpired()
  }

  /**
   * Schedule the next token refresh based on the JWT token's expiry time
   */
  private scheduleNextRefresh(keycloak: Keycloak): void {
    if (this.refreshTimeout) clearTimeout(this.refreshTimeout)

    const accessTokenExp = keycloak.tokenParsed?.exp

    if (!accessTokenExp) {
      return
    }

    const now = Math.floor(Date.now() / 1000)
    const expiresIn = accessTokenExp - now

    if (expiresIn <= 0) {
      void this.callbacks.onSessionExpired()
      return
    }

    const buffer = Math.min(ACCESS_TOKEN_REFRESH_BUFFER_SECONDS, expiresIn)
    const refreshInSeconds = Math.max(expiresIn - buffer, 1)

    this.refreshTimeout = setTimeout(() => this.refreshToken(keycloak), refreshInSeconds * 1000)
  }

  private scheduleCountdown(keycloak: Keycloak): void {
    if (this.countdownTimeout) clearTimeout(this.countdownTimeout)

    const refreshTokenExp = keycloak.refreshTokenParsed?.exp
    if (!refreshTokenExp) {
      return
    }

    const now = Math.floor(Date.now() / 1000)
    const expiresIn = refreshTokenExp - now

    if (expiresIn <= 0) {
      this.handleCountdownExpired()
      return
    }

    if (expiresIn <= REFRESH_TOKEN_EXPIRY_WARNING_SECONDS) {
      this.showCountdown()
      return
    }

    const timeUntilCountdown = expiresIn - REFRESH_TOKEN_EXPIRY_WARNING_SECONDS
    this.countdownTimeout = setTimeout(() => this.showCountdown(), timeUntilCountdown * 1000)
  }

  private showCountdown(): void {
    if (this.countdownActive) return

    if (this.refreshTimeout) clearTimeout(this.refreshTimeout)

    this.closeCountdown()
    this.countdownActive = true

    this.countdownSnackbarKey = enqueueSnackbar('Session expiring', {
      persist: true,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      content: () =>
        createElement(SessionExpirySnackbar, {
          getTimeRemaining: () => this.getTimeRemaining(),
          onExtendSession: () => this.handleExtendSession(),
          onTimeExpired: () => this.handleCountdownExpired()
        })
    })
  }

  private getTimeRemaining(): number {
    if (!this.keycloakInstance?.refreshTokenParsed?.exp) {
      return 0
    }

    return Math.max(0, this.keycloakInstance.refreshTokenParsed.exp - Math.floor(Date.now() / 1000))
  }

  private handleCountdownExpired(): void {
    this.closeCountdown()
    this.callbacks.onSessionExpired()
  }

  private async handleAccessTokenExpired(keycloak: Keycloak): Promise<void> {
    try {
      await keycloak.updateToken(0)

      if (keycloak.token) {
        this.callbacks.onTokenRefreshed(keycloak.token)
        this.scheduleNextRefresh(keycloak)
        this.scheduleCountdown(keycloak)
        return
      }
    } catch (error) {
      logger.error(error, 'Token refresh failed after expiry')
    }

    this.closeCountdown()
    await this.callbacks.onSessionExpired()
  }

  private async handleExtendSession(): Promise<void> {
    if (!this.keycloakInstance) return

    this.closeCountdown()

    try {
      await this.keycloakInstance.updateToken(TOKEN_MIN_VALIDITY_SECONDS)

      if (this.keycloakInstance.token) {
        this.callbacks.onTokenRefreshed(this.keycloakInstance.token)
        enqueueSnackbar('Session extended successfully', {
          variant: 'success',
          autoHideDuration: 3000
        })
        this.scheduleNextRefresh(this.keycloakInstance)
        this.scheduleCountdown(this.keycloakInstance)
      }
    } catch (error) {
      logger.error(error, 'Failed to extend session')
      notifyError('Failed to extend session. Please log in again.')
      await this.callbacks.onSessionExpired()
    }
  }
}
